import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  useDeleteAclItem,
  useGetAclItems,
} from '../../../services/redux/api/aclApi';
import { getCellReducers } from './helpers/getCellReducers';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { IAcl } from '../../../types/interfaces/acl';
import { useAccess } from '../../../hooks/useAccess';

export function useAclComponent(classes) {
  const { t } = useTranslation();
  const { data: dbAclItems } = useGetAclItems();
  const [itemForDelete, setItemForDelete] = useState<IAcl>(null);
  const [itemForEditing, setItemForEditing] = useState<IAcl>(null);
  const [showEditAclModal, setShowEditAclModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteAclItem] = useDeleteAclItem();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const filteredValues = useMemo(
    () =>
      dbAclItems?.filter((aclItem) => {
        if (!filter) return true;

        const filterValue = filter.toLowerCase();

        if (aclItem.role) {
          if (
            aclItem.role.nameKey.toLowerCase().includes(filterValue) ||
            t(`common.${aclItem.role?.name}`)
              .toLowerCase()
              .includes(filterValue)
          ) {
            return true;
          }
        }

        if (aclItem.user) {
          if (
            aclItem.user.firstname.toLowerCase().includes(filterValue) ||
            aclItem.user.lastname.toLowerCase().includes(filterValue) ||
            aclItem.user.email.toLowerCase().includes(filterValue)
          ) {
            return true;
          }
        }

        if (aclItem.permission.toLowerCase().includes(filterValue)) return true;

        return false;
      }),
    [dbAclItems, filter],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.acl?.canEdit ?? false,
      canDelete: access?.acl?.canDelete ?? false,
    },
    (acl: IAcl) => {
      setItemForEditing(acl);
      setShowEditAclModal(true);
    },
    (acl: IAcl) => {
      setItemForDelete(acl);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.ACL_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteAclItem({ id: itemForDelete.id });
  }

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditAclModal,
    setShowEditAclModal,
    itemForEditing,
    setItemForEditing,
  };
}
